import React, { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useLocation } from "react-router-dom";
import { Spinner } from "components/ui";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { PASSWORD_EXP, PASSWORD_REQUIREMENTS } from "utils/validation";
import { useDispatch } from "react-redux";
import { resetExpiredPassword } from "actions/auth";
import useTranslate from "hooks/use-translate-language";
import useDocumentTitle from "hooks/use-document-title";
import { translateUsingBrowser } from "utils/misc";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const ExpiredPassword = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const { state = {} } = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);

  useDocumentTitle(translateUsingBrowser("passwordExpired"));

  const handleClickShowPassword = () => setShowPassword(show => !show);
  const handleClickShowPasswordConfirmation = () =>
    setShowPasswordConfirmation(show => !show);

  const getFieldError = (field, errors, touched) => {
    return touched[field] ? errors[field] : "";
  };
  return (
    <div className="login-screen" role="main">
      <div className="login-background" aria-hidden="true">
        <div className="white-background"></div>
        <div className="color-background"></div>
      </div>

      <header className="login-screen-header">
        <div
          className="login-logo"
          role="img"
          aria-label="My Sleep Coach Powered by WellSky"
        />
      </header>
      <div className="login-container">
        <div className="login-form-card" role="form">
          <h1 className="login-card-header" id="forgot-password-title">
            {translateUsingBrowser("passwordExpired")}
          </h1>

          <Formik
            initialValues={{
              ...state,
              password: "",
              password_confirmation: ""
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string().required("email required"),
              current_password: Yup.string().required(
                "current password required"
              ),
              password: Yup.string()
                .required("Required.")
                .matches(PASSWORD_EXP, PASSWORD_REQUIREMENTS),
              password_confirmation: Yup.string()
                .required("Required.")
                .oneOf([Yup.ref("password")], translate("passwordsDoNotMatch"))
            })}
            onSubmit={async values => {
              await dispatch(resetExpiredPassword(values));
            }}
            enableReinitialize={true}
          >
            {({
              isSubmitting,
              isValid,
              handleChange,
              handleSubmit,
              errors,
              touched,
              handleBlur,
              values
            }) => (
              <form onSubmit={handleSubmit} aria-labelledby="password-expired">
                <div className="form-field">
                  <TextField
                    id="password"
                    label={translateUsingBrowser("newPassword")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    name="password"
                    error={!!getFieldError("password", errors, touched)}
                    helperText={getFieldError("password", errors, touched)}
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword ? "Hide password" : "Show password"
                              }
                              onClick={handleClickShowPassword}
                              edge="end"
                              type="button"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    }}
                  />
                </div>
                <div className="form-field">
                  <TextField
                    id="password_confirmation"
                    label={translateUsingBrowser("confirmNewPassword")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password_confirmation}
                    name="password_confirmation"
                    error={
                      !!getFieldError("password_confirmation", errors, touched)
                    }
                    helperText={getFieldError(
                      "password_confirmation",
                      errors,
                      touched
                    )}
                    type={showPasswordConfirmation ? "text" : "password"}
                    autoComplete="new-password"
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPasswordConfirmation
                                  ? "Hide password"
                                  : "Show password"
                              }
                              onClick={handleClickShowPasswordConfirmation}
                              edge="end"
                              type="button"
                            >
                              {showPasswordConfirmation ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    }}
                  />
                </div>
                {isSubmitting ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 120
                    }}
                    role="status"
                    aria-label="Logging in"
                  >
                    <Spinner />
                  </div>
                ) : (
                  <div className="login-form-actions">
                    <Button
                      variant="contained"
                      color="primary"
                      id="reset-password-button"
                      type="submit"
                      disabled={!isValid}
                    >
                      {translateUsingBrowser("changePassword")}
                    </Button>
                  </div>
                )}
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ExpiredPassword;
